// OPSLAYOUT: the COO "Operational Tasks" presentation, as shared components.
// ─────────────────────────────────────────────────────────────────────────────
//
// WHY THIS FILE EXISTS. The confirmed pattern is the COO's Operational Tasks view
// (Dashboard.js, activeSection === 'tasks'): a title with a total count, pill tabs each carrying
// its own count, and a table of Task / Employee / Due / Due date with clickable rows.
//
// THE COO VIEW'S OWN STYLES ARE INLINE LOCALS INSIDE AN IIFE (tabBase, tabOn, badge, declared
// inside the `activeSection === 'tasks'` block). They are not exported and cannot be imported.
// Extracting them from Dashboard.js would mean EDITING Dashboard.js, which this task forbids
// absolutely. So the token VALUES are copied here verbatim, and a source guard asserts they stay
// equal to the COO's: if either side is ever restyled, that test fails and names the divergence,
// rather than the two drifting apart silently.
//
// THE COO VIEW IS NOT CHANGED, NOT IMPORTED FROM, AND NOT REFACTORED. Dashboard.js stays
// byte-identical to HEAD, asserted. This module is consumed only by the compliance shell.
//
// WHAT IS DELIBERATELY NOT COPIED: the COO's gold TITLE badge (s.goldBadge) and its "Mark
// complete" column. The title count is rendered by the caller, because the two shells have
// different title chrome, and the brief's "no green or gold count badges" applies to the TAB
// counts, which are the muted/white pattern below.
import React from 'react';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

// The pill tokens, copied verbatim from Dashboard.js's Operational Tasks block. Functions of
// isDarkMode because the originals are.
export const opsTabBase = (isDarkMode) => ({
  backgroundColor: 'transparent',
  border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`,
  borderRadius: '6px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
  color: isDarkMode ? '#9ba8c0' : '#888',
  display: 'flex', alignItems: 'center', gap: '5px',
});

export const opsTabOn = (isDarkMode) => ({
  ...opsTabBase(isDarkMode), backgroundColor: '#1a2744', border: '1px solid transparent',
  color: '#fff', fontWeight: 560,
});

// TASKHEADERTIDY: the count is NOT enclosed in a pill. It inherits the tab's white when selected
// and keeps a muted grey otherwise, with tabular figures so the digits line up against the label.
// No green and no gold, per the confirmed decision.
export const OpsTabCount = ({ n, on, isDarkMode }) => (
  <span style={{
    color: on ? '#fff' : (isDarkMode ? '#9ba8c0' : '#666'),
    fontSize: '11px', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
  }}>{n}</span>
);

// A row of pill tabs. `tabs` is [{ id, label, count }]; counts render even at ZERO, because the
// label's job is to show what is in the tab you are NOT looking at.
export const OpsTabs = ({ tabs, active, onSelect, isDarkMode }) => (
  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
    {tabs.map(t => (
      <button
        key={t.id}
        type="button"
        style={active === t.id ? opsTabOn(isDarkMode) : opsTabBase(isDarkMode)}
        aria-pressed={active === t.id}
        onClick={() => onSelect(t.id)}
      >
        {t.label} <OpsTabCount n={t.count} on={active === t.id} isDarkMode={isDarkMode} />
      </button>
    ))}
  </div>
);

// THE DUE CELL, matching the COO's own rendering exactly:
//   no due date      -> "No due date", muted
//   days_overdue >= 0 -> "{n}d overdue", red and semibold
//   otherwise        -> the remaining days, muted
//
// The COO view prints a bare "Upcoming" for the not-overdue case, deliberately, because Python's
// negative-timedelta floor skews a naive day count by up to a day. The compliance brief asks for
// "days remaining", so that is shown here from the SAME signed days_overdue value (remaining =
// -days_overdue), which keeps the two readings of the same number consistent rather than
// introducing a second derivation. The COO view itself is untouched.
export const OpsDueCell = ({ dueDate, daysOverdue, isDarkMode }) => {
  const muted = { fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#aaa' };
  if (!dueDate) return <span style={muted}>No due date</span>;
  if (daysOverdue >= 0) {
    return (
      <span style={{ fontSize: '12px', color: '#A63A3A', fontWeight: 600 }}>
        {daysOverdue}d overdue
      </span>
    );
  }
  return <span style={muted}>{Math.abs(daysOverdue)}d left</span>;
};

export const OpsDateCell = ({ value, isDarkMode }) => (
  <span style={{ fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#aaa' }}>
    {value ? toDisplayDate(value).toLocaleDateString() : '-'}
  </span>
);

// The table head and body chrome, so both compliance views share one table shape.
export const opsTh = (isDarkMode) => ({
  textAlign: 'left', fontSize: '11px', fontWeight: 600,
  color: isDarkMode ? '#9ba8c0' : '#aaa', padding: '6px 10px',
  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}`,
  textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
});

export const opsTd = (isDarkMode) => ({
  padding: '10px 10px', fontSize: '13px', color: isDarkMode ? '#9ba8c0' : '#555',
});

export const opsTdStrong = (isDarkMode) => ({
  padding: '10px 10px', fontSize: '13px', fontWeight: 500,
  color: isDarkMode ? '#e8e4d8' : '#1a1a2e',
});

export const opsTr = (isDarkMode, clickable) => ({
  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.08)' : '#f4f4f4'}`,
  cursor: clickable ? 'pointer' : 'default',
});

export const opsEmpty = (isDarkMode) => ({
  fontSize: '13px', color: isDarkMode ? '#9ba8c0' : '#667085',
  fontStyle: 'italic', margin: '0 0 4px 0',
});

// A whole table: `columns` is [{ key, label }], `rows` the data, `cell` renders one cell and
// `onRowClick` returns a handler or null per row (null leaves the row non-interactive, which is
// how the compliance Tasks view already treats a type with no destination).
export const OpsTable = ({ columns, rows, cell, rowKey, onRowClick, emptyText, isDarkMode }) => {
  if (!rows || rows.length === 0) {
    return <p style={opsEmpty(isDarkMode)}>{emptyText}</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{columns.map(c => <th key={c.key} style={opsTh(isDarkMode)}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const handler = onRowClick ? onRowClick(row) : null;
            return (
              <tr
                key={rowKey ? rowKey(row, i) : i}
                style={opsTr(isDarkMode, !!handler)}
                onClick={handler || undefined}
              >
                {columns.map(c => (
                  <td key={c.key} style={c.strong ? opsTdStrong(isDarkMode) : opsTd(isDarkMode)}>
                    {cell(row, c.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
